import type { Vehicle, ApiResponse, VehicleAdjustment, RentAddition } from '../types'
import { db, _, ensureLogin, app } from './tcbClient'

type VehicleStatusKey = 'available' | 'rented' | 'maintenance' | 'inactive'

type ReceiptItem = {
  vehicleId?: string
  plate?: string
  expectedAmount?: number
  receivedAmount?: number
  dueDate?: string
  startDate?: string
  rentPeriod?: string
  chargeRangeStart?: string
  chargeRangeEnd?: string
  status?: string
}

type OperatorInfo = {
  userId?: string
  username?: string
  nickName?: string
  role?: string
  info?: string
}

const normalizeReceipt = (doc: any) => {
  const items: ReceiptItem[] = Array.isArray(doc?.items) && doc.items.length
    ? doc.items.map((it: any) => ({
        vehicleId: it.vehicleId || it._id || it.id,
        plate: it.plate,
        expectedAmount: Number(it.expectedAmount ?? it.amount ?? it.receivedAmount ?? doc.amount ?? 0),
        receivedAmount: Number(it.receivedAmount ?? it.verifiedAmount ?? it.amount ?? 0),
        dueDate: it.dueDate || doc.dueDate,
        startDate: it.startDate || doc.startDate,
        rentPeriod: it.rentPeriod || doc.rentPeriod,
        chargeRangeStart: it.chargeRangeStart || doc.chargeRangeStart,
        chargeRangeEnd: it.chargeRangeEnd || doc.chargeRangeEnd,
        status: it.status || doc.status || 'submitted'
      }))
    : [{
        vehicleId: doc.vehicleId || doc.id,
        plate: doc.plate,
        expectedAmount: Number(doc.expectedAmount ?? doc.amount ?? 0),
        receivedAmount: Number(doc.verifiedAmount ?? doc.amount ?? 0),
        dueDate: doc.dueDate,
        startDate: doc.startDate,
        rentPeriod: doc.rentPeriod,
        chargeRangeStart: doc.chargeRangeStart,
        chargeRangeEnd: doc.chargeRangeEnd,
        status: doc.status || 'submitted'
      }]

  const totalExpected = items.reduce((sum, it) => sum + (Number(it.expectedAmount) || 0), 0)
  const totalReceived = items.reduce((sum, it) => sum + (Number(it.receivedAmount) || 0), 0)

  return {
    ...doc,
    items,
    amount: totalExpected || doc.amount,
    verifiedAmount: totalReceived || doc.verifiedAmount,
    status: doc.status || 'submitted'
  }
}

const mapVehicle = (doc: any): Vehicle => {
    const now = new Date().toISOString()
    const pricingHistory = Array.isArray(doc.pricingHistory)
      ? doc.pricingHistory.map((p: any) => ({
          startDate: p.startDate || p.effectiveDate || '',
          rentAmount: Number(p.rentAmount ?? p.amount ?? 0) || 0,
          rentPeriod: p.rentPeriod || doc.rentPeriod,
          rentPeriodMonths: p.rentPeriodMonths
        }))
      : []
  return {
    id: doc.id || doc._id,
    _id: doc._id,
    plate: doc.plate || doc.forklift_no || '',
    type: doc.type || doc.forklift_model || '',
    company: doc.company || doc.tenant || '',
    phone: doc.phone || '',
    status: (doc.status || 'available') as any,
    dueDate: doc.dueDate || '',
    reminderDate: doc.reminderDate || '',
    reminderPeriod: doc.reminderPeriod || '',
    reminderPeriodMonths: doc.reminderPeriodMonths,
    reminderAmount: Number(doc.reminderAmount ?? 0) || 0,
    depositAmount: doc.depositAmount ?? doc.deposit ?? 0,
    address: doc.address || '',
    rentAmount: doc.rentAmount || doc.monthly_rent,
    startDate: doc.startDate || doc.rent_start_date || '',
    contactPerson: doc.contactPerson || doc.contact_person || '',
    contactPerson2: doc.contactPerson2 || doc.contact_person2 || '',
    phone2: doc.phone2 || '',
    area: doc.area || doc.region || '',
    configuration: doc.configuration,
    returnSigned: Boolean(doc.returnSigned),
    contractFiles: Array.isArray(doc.contractFiles) ? doc.contractFiles : [],
    rentPeriod: doc.rentPeriod,
    rentPeriodMonths: doc.rentPeriodMonths,
    pricingHistory,
    currentLeaseId: doc.currentLeaseId,
    leaseHistory: Array.isArray(doc.leaseHistory) ? doc.leaseHistory.map((l: any) => ({
      leaseId: l.leaseId || l.id || '',
      company: l.company || '',
      contactPerson: l.contactPerson || '',
      phone: l.phone || '',
      contactPerson2: l.contactPerson2 || '',
      phone2: l.phone2 || '',
      startDate: l.startDate || '',
      endDate: l.endDate || '',
      rentAmount: l.rentAmount,
      rentPeriod: l.rentPeriod,
      rentPeriodMonths: l.rentPeriodMonths
    })) : [],
    notes: doc.notes || '',
    createdAt: doc.createdAt || doc.created_time || now,
    updatedAt: doc.updatedAt || doc.updated_time || now
  }
}

const readOperatorFromStorage = (): OperatorInfo => {
  try {
    if (typeof window === 'undefined') return {}
    const raw = window.localStorage.getItem('auth-storage')
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    const user = parsed?.state?.user || parsed?.user
    if (!user) return {}
    return {
      userId: user.id,
      username: user.username,
      nickName: user.nickname || user.nickName,
      role: user.role,
      info: user.info
    }
  } catch {
    return {}
  }
}

const pickOperatorFromPayload = (payload: any): OperatorInfo => {
  if (!payload || typeof payload !== 'object') return {}
  return {
    userId: payload?.operatorId || payload?.userId || payload?.makerId || payload?.cashierId || payload?.bossId || payload?.accountantId,
    username: payload?.username,
    nickName: payload?.operatorName || payload?.createdBy || payload?.cashierName || payload?.bossName || payload?.accountantName || payload?.nickName,
    role: payload?.role,
    info: payload?.info
  }
}

class SimpleDirectCloudApi {
  private async writeOperationLog(params: {
    action: 'create' | 'update' | 'delete'
    collection: string
    targetId?: string
    payload?: any
  }) {
    try {
      const byPayload = pickOperatorFromPayload(params.payload)
      const byStorage = readOperatorFromStorage()
      const actor: OperatorInfo = {
        userId: byPayload.userId || byStorage.userId,
        username: byPayload.username || byStorage.username,
        nickName: byPayload.nickName || byStorage.nickName,
        role: byPayload.role || byStorage.role,
        info: byPayload.info || byStorage.info
      }
      await db.collection('operation_logs').add({
        _id: `op-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        action: params.action,
        collection: params.collection,
        targetId: params.targetId || '',
        actor,
        payloadSnapshot: params.payload || {},
        createdAt: new Date().toISOString()
      } as any)
    } catch (err) {
      // Non-blocking: audit write failure should not break business flow
      console.warn('[operation_logs] write failed', err)
    }
  }

  async init(): Promise<{ success: boolean; error?: string }> {
    try {
      await ensureLogin()
      return { success: true }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async checkConnection(): Promise<boolean> {
    try {
      await ensureLogin()
      return true
    } catch {
      return false
    }
  }

  async getAllVehicles(): Promise<ApiResponse<Vehicle[]>> {
    try {
      await ensureLogin()
      const res = await db.collection('jxc').limit(500).get()
      const data = Array.isArray(res?.data) ? res.data : []
      const vehicles = data.map(mapVehicle)
      return { success: true, data: vehicles }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async getVehiclesByPage(status: string, page: number, pageSize: number): Promise<ApiResponse<Vehicle[]>> {
    try {
      await ensureLogin()
      const query: any = {}
      if (status && status !== 'all') query.status = status
      const res = await db
        .collection('jxc')
        .where(query)
        .orderBy('updatedAt', 'desc')
        .skip(Math.max(0, (page - 1) * pageSize))
        .limit(pageSize)
        .get()
      const data = Array.isArray(res?.data) ? res.data : []
      return { success: true, data: data.map(mapVehicle), hasMore: data.length === pageSize }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async searchVehicles(keyword: string): Promise<ApiResponse<Vehicle[]>> {
    try {
      await ensureLogin()
      const lower = keyword.toLowerCase()
      const res = await db.collection('jxc').get()
      const data = Array.isArray(res?.data) ? res.data : []
      const matched = data.map(mapVehicle).filter(v => {
        const plate = String(v.plate || '').toLowerCase()
        const company = String(v.company || '').toLowerCase()
        const contact = String(v.contactPerson || '').toLowerCase()
        const contact2 = String(v.contactPerson2 || '').toLowerCase()
        const phone = String(v.phone || '')
        const phone2 = String(v.phone2 || '')
        return (
          plate.includes(lower) ||
          company.includes(lower) ||
          contact.includes(lower) ||
          contact2.includes(lower) ||
          phone.includes(keyword) ||
          phone2.includes(keyword)
        )
      })
      return { success: true, data: matched }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async addVehicle(vehicleData: Omit<Vehicle, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<Vehicle>> {
    try {
      await ensureLogin()
      const existingRes = await db.collection('jxc').where({ plate: vehicleData.plate }).limit(1).get()
      const existing = Array.isArray(existingRes?.data) ? existingRes.data : []
      if (existing.length) {
        return { success: false, error: 'Vehicle exists' }
      }
      const now = new Date().toISOString()
      const id = `veh-${Date.now()}`
      const doc: Vehicle = {
        ...vehicleData,
        id,
        _id: id,
        status: (vehicleData as any).status || 'available',
        createdAt: now,
        updatedAt: now
      }
      await db.collection('jxc').add(doc)
      await this.writeOperationLog({
        action: 'create',
        collection: 'jxc',
        targetId: id,
        payload: doc
      })
      return { success: true, data: doc }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async updateVehicle(id: string, updates: Partial<Vehicle>): Promise<ApiResponse> {
    try {
      await ensureLogin()
      const docId = updates._id || id
      const payload: any = { ...updates }
      // 云端不允许更新 _id
      delete payload._id
      if (payload.pricingHistory) {
        payload.pricingHistory = (payload.pricingHistory as any[]).map(p => {
          const cleaned: any = {
            startDate: p.startDate,
            rentAmount: p.rentAmount,
            rentPeriod: p.rentPeriod
          }
          if (p.rentPeriod === 'custom' && p.rentPeriodMonths) cleaned.rentPeriodMonths = p.rentPeriodMonths
          return cleaned
        })
      }
      Object.keys(payload).forEach(key => {
        if (payload[key] === undefined) delete payload[key]
      })
      payload.updatedAt = new Date()

      const res = await db.collection('jxc').doc(docId).update(payload)
      console.log('updateVehicle result', res)
      await this.writeOperationLog({
        action: 'update',
        collection: 'jxc',
        targetId: docId,
        payload
      })
      return { success: true }
    } catch (err: any) {
      console.error('updateVehicle error', err)
      return { success: false, error: err?.message }
    }
  }

  async deleteVehicle(id: string): Promise<ApiResponse> {
    try {
      await ensureLogin()
      await db.collection('jxc').doc(id).remove()
      await this.writeOperationLog({
        action: 'delete',
        collection: 'jxc',
        targetId: id
      })
      return { success: true }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async getStatistics(): Promise<ApiResponse<any>> {
    try {
      await ensureLogin()
      const res = await db.collection('jxc').get()
      const data = Array.isArray(res?.data) ? res.data : []
      const vehicles = data.map(mapVehicle)
      const statusCount: Record<VehicleStatusKey, number> = {
        available: 0,
        rented: 0,
        maintenance: 0,
        inactive: 0
      }

      let expiring = 0
      let expired = 0
      const now = new Date()
      const tenDaysLater = new Date(now.getTime() + 10 * 24 * 60 * 60 * 1000)

      vehicles.forEach(v => {
        const key = (v.status as VehicleStatusKey) || 'available'
        if (statusCount[key] !== undefined) {
          statusCount[key] += 1
        }
        const due = new Date(v.dueDate)
        if (!isNaN(due.getTime())) {
          if (due < now) expired += 1
          else if (due <= tenDaysLater) expiring += 1
        }
      })

      return {
        success: true,
        data: {
          total: vehicles.length,
          available: statusCount.available,
          rented: statusCount.rented,
          expiring,
          expired
        }
      }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async getUserByNickName(nickName: string): Promise<ApiResponse<any>> {
    try {
      await ensureLogin()
      const res = await db.collection('user').where({ nickName }).limit(1).get()
      const data = Array.isArray(res?.data) ? res.data : []
      if (!data.length) {
        return { success: false, error: 'User not found' }
      }
      return { success: true, data: { ...data[0], _id: data[0]._id || data[0].id } }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async getAvailableUsers(): Promise<ApiResponse<any[]>> {
    try {
      await ensureLogin()
      const res = await db.collection('user').get()
      const data = Array.isArray(res?.data) ? res.data : []
      return { success: true, data }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async addReceipt(data: any): Promise<ApiResponse<any>> {
    try {
      await ensureLogin()
      const now = new Date().toISOString()
      const id = data._id || data.id || `rcpt-${Date.now()}`
      const items: ReceiptItem[] = Array.isArray(data.items) && data.items.length
        ? data.items.map((it: any) => ({
            vehicleId: it.vehicleId || it._id || it.id,
            plate: it.plate,
            expectedAmount: Number(it.expectedAmount ?? it.amount ?? 0),
            receivedAmount: Number(it.receivedAmount ?? it.amount ?? 0),
            dueDate: it.dueDate || data.dueDate,
            startDate: it.startDate || data.startDate || '',
            rentPeriod: it.rentPeriod || data.rentPeriod || '',
            chargeRangeStart: it.chargeRangeStart || data.chargeRangeStart,
            chargeRangeEnd: it.chargeRangeEnd || data.chargeRangeEnd,
            status: it.status || data.status || 'submitted'
          }))
        : [{
            vehicleId: data.vehicleId,
            plate: data.plate,
            expectedAmount: Number(data.expectedAmount ?? data.amount ?? 0),
            receivedAmount: Number(data.receivedAmount ?? data.amount ?? 0),
            dueDate: data.dueDate,
            startDate: data.startDate || '',
            rentPeriod: data.rentPeriod || '',
            chargeRangeStart: data.chargeRangeStart,
            chargeRangeEnd: data.chargeRangeEnd,
            status: data.status || 'submitted'
          }]

      const doc = {
        ...data,
        _id: id,
        id,
        status: data.status || 'submitted',
        createdAt: data.createdAt || now,
        updatedAt: now,
        startDate: data.startDate || '',
        rentPeriod: data.rentPeriod || '',
        items
      }
      await db.collection('receipts').add(doc)
      await this.writeOperationLog({
        action: 'create',
        collection: 'receipts',
        targetId: id,
        payload: doc
      })
      return { success: true, data: doc }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async updateReceipt(id: string, updates: any): Promise<ApiResponse<any>> {
    try {
      await ensureLogin()
      const docId = updates._id || id
      await db.collection('receipts').doc(docId).update({
        ...updates,
        updatedAt: new Date().toISOString()
      })
      await this.writeOperationLog({
        action: 'update',
        collection: 'receipts',
        targetId: docId,
        payload: updates
      })
      return { success: true }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async getReceipts(status?: string): Promise<ApiResponse<any[]>> {
    try {
      await ensureLogin()
      const collection = db.collection('receipts')
      const res = status && status !== 'all'
        ? await collection.where({ status }).get()
        : await collection.get()
      const data = Array.isArray(res?.data) ? res.data : []
      const normalized = data.map(normalizeReceipt)
      return { success: true, data: normalized }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async getUserByIdentity(value: string): Promise<ApiResponse<any>> {
    try {
      await ensureLogin()
      const tryFields = [
        { nickName: value },
        { username: value },
        { id: value },
        { _id: value },
        { uid: value }
      ]
      for (const query of tryFields) {
        const res = await db.collection('user').where(query as any).limit(1).get()
        const data = Array.isArray(res?.data) ? res.data : []
        if (data.length) {
          return { success: true, data: { ...data[0], _id: data[0]._id || data[0].id } }
        }
      }
      return { success: false, error: 'User not found' }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async addUserProfile(data: { uid?: string; username?: string; nickName?: string; role?: string }): Promise<ApiResponse<any>> {
    try {
      await ensureLogin()
      const now = new Date().toISOString()
      const doc = {
        _id: data.uid || data.username || `user-${Date.now()}`,
        uid: data.uid,
        username: data.username || data.nickName,
        nickName: data.nickName || data.username || 'user',
        role: data.role || 'C',
        isActive: true,
        createTime: now
      }
      await db.collection('user').add(doc as any)
      await this.writeOperationLog({
        action: 'create',
        collection: 'user',
        targetId: doc._id,
        payload: doc
      })
      return { success: true, data: doc }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async getVehicleAdjustments(vehicleId?: string): Promise<ApiResponse<VehicleAdjustment[]>> {
    try {
      await ensureLogin()
      const collection = db.collection('vehicle_adjustments')
      const res = vehicleId
        ? await collection.where({ vehicleId }).get()
        : await collection.get()
      const data = Array.isArray(res?.data) ? res.data : []
      return { success: true, data }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async updateVehicleAdjustment(id: string, updates: Partial<VehicleAdjustment>): Promise<ApiResponse> {
    try {
      await ensureLogin()
      const docId = updates._id || id
      const payload: any = { ...updates }
      delete payload._id
      payload.updatedAt = new Date().toISOString()
      await db.collection('vehicle_adjustments').doc(docId).update(payload)
      await this.writeOperationLog({
        action: 'update',
        collection: 'vehicle_adjustments',
        targetId: docId,
        payload
      })
      return { success: true }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async deleteVehicleAdjustment(id: string): Promise<ApiResponse> {
    try {
      await ensureLogin()
      await db.collection('vehicle_adjustments').doc(id).remove()
      await this.writeOperationLog({
        action: 'delete',
        collection: 'vehicle_adjustments',
        targetId: id
      })
      return { success: true }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async getVehicleRentAdditions(vehicleId?: string): Promise<ApiResponse<RentAddition[]>> {
    try {
      await ensureLogin()
      const collection = db.collection('vehicle_rent_additions')
      const res = vehicleId
        ? await collection.where({ vehicleId }).get()
        : await collection.get()
      const data = Array.isArray(res?.data) ? res.data : []
      return { success: true, data }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async updateVehicleRentAddition(id: string, updates: Partial<RentAddition>): Promise<ApiResponse> {
    try {
      await ensureLogin()
      const docId = updates._id || id
      const payload: any = { ...updates }
      delete payload._id
      payload.updatedAt = new Date().toISOString()
      await db.collection('vehicle_rent_additions').doc(docId).update(payload)
      await this.writeOperationLog({
        action: 'update',
        collection: 'vehicle_rent_additions',
        targetId: docId,
        payload
      })
      return { success: true }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async deleteVehicleRentAddition(id: string): Promise<ApiResponse> {
    try {
      await ensureLogin()
      await db.collection('vehicle_rent_additions').doc(id).remove()
      await this.writeOperationLog({
        action: 'delete',
        collection: 'vehicle_rent_additions',
        targetId: id
      })
      return { success: true }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async addVehicleAdjustment(data: VehicleAdjustment): Promise<ApiResponse<VehicleAdjustment>> {
    try {
      await ensureLogin()
      const now = new Date().toISOString()
      const id = data._id || data.id || `adj-${Date.now()}`
      const doc: VehicleAdjustment = {
        ...data,
        id,
        _id: id,
        createdAt: data.createdAt || now
      }
      await db.collection('vehicle_adjustments').add(doc)
      await this.writeOperationLog({
        action: 'create',
        collection: 'vehicle_adjustments',
        targetId: id,
        payload: doc
      })
      return { success: true, data: doc }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async addVehicleRentAddition(data: RentAddition): Promise<ApiResponse<RentAddition>> {
    try {
      await ensureLogin()
      const now = new Date().toISOString()
      const id = data._id || data.id || `rent-${Date.now()}`
      const doc: RentAddition = {
        ...data,
        id,
        _id: id,
        type: 'rent_addition',
        createdAt: data.createdAt || now
      }
      await db.collection('vehicle_rent_additions').add(doc)
      await this.writeOperationLog({
        action: 'create',
        collection: 'vehicle_rent_additions',
        targetId: id,
        payload: doc
      })
      return { success: true, data: doc }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async addLeaseForm(data: any): Promise<ApiResponse<any>> {
    try {
      await ensureLogin()
      const now = new Date().toISOString()
      const id = data._id || data.id || `lease-${Date.now()}`
      const doc = {
        ...data,
        _id: id,
        id,
        status: data.status || 'pending',
        createdAt: data.createdAt || now,
        updatedAt: now
      }
      await db.collection('lease_forms').add(doc)
      await this.writeOperationLog({
        action: 'create',
        collection: 'lease_forms',
        targetId: id,
        payload: doc
      })
      return { success: true, data: doc }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async getLeaseForms(status?: string): Promise<ApiResponse<any[]>> {
    try {
      await ensureLogin()
      const collection = db.collection('lease_forms')
      const res = status
        ? await collection.where({ status }).get()
        : await collection.get()
      const data = Array.isArray(res?.data) ? res.data : []
      return { success: true, data }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async updateLeaseForm(id: string, updates: any): Promise<ApiResponse<any>> {
    try {
      await ensureLogin()
      const docId = updates._id || id
      await db.collection('lease_forms').doc(docId).update({
        ...updates,
        updatedAt: new Date().toISOString()
      })
      await this.writeOperationLog({
        action: 'update',
        collection: 'lease_forms',
        targetId: docId,
        payload: updates
      })
      return { success: true }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async addRentAdjustRequest(data: any): Promise<ApiResponse<any>> {
    try {
      await ensureLogin()
      const now = new Date().toISOString()
      const id = data._id || data.id || `rent-adjust-${Date.now()}`
      const doc = {
        ...data,
        _id: id,
        id,
        status: data.status || 'pending',
        createdAt: data.createdAt || now,
        updatedAt: now
      }
      await db.collection('rent_adjust_requests').add(doc)
      await this.writeOperationLog({
        action: 'create',
        collection: 'rent_adjust_requests',
        targetId: id,
        payload: doc
      })
      return { success: true, data: doc }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async getRentAdjustRequests(status?: string): Promise<ApiResponse<any[]>> {
    try {
      await ensureLogin()
      const collection = db.collection('rent_adjust_requests')
      const res = status
        ? await collection.where({ status }).get()
        : await collection.get()
      const data = Array.isArray(res?.data) ? res.data : []
      return { success: true, data }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async updateRentAdjustRequest(id: string, updates: any): Promise<ApiResponse<any>> {
    try {
      await ensureLogin()
      const docId = updates._id || id
      await db.collection('rent_adjust_requests').doc(docId).update({
        ...updates,
        updatedAt: new Date().toISOString()
      })
      await this.writeOperationLog({
        action: 'update',
        collection: 'rent_adjust_requests',
        targetId: docId,
        payload: updates
      })
      return { success: true }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }

  async getOperationLogs(limit = 500): Promise<ApiResponse<any[]>> {
    try {
      await ensureLogin()
      const res = await db
        .collection('operation_logs')
        .orderBy('createdAt', 'desc')
        .limit(Math.max(1, Math.min(limit, 2000)))
        .get()
      const data = Array.isArray(res?.data) ? res.data : []
      return { success: true, data }
    } catch (err: any) {
      return { success: false, error: err?.message }
    }
  }
}

export const simpleDirectCloudApi = new SimpleDirectCloudApi()
export type { ApiResponse }
